/**
 * Shared cookie-consent reader for the React SPA.
 *
 * Both Microsoft Clarity (`clarity.ts`) and Google Analytics (`analytics.ts`)
 * gate on the SAME decision: the `cookie_consent_v1` localStorage key written
 * by the marketing-site banner (harmny/src/scripts/cookie-consent.ts) and the
 * in-app auto-grant (clarity.ts → autoGrantConsentForAuthenticatedUser). Both
 * sites live on `harmny.ai`, so localStorage is shared by origin.
 *
 * This module is the single source of truth for "may we run statistics
 * tooling?" — keep the shape in sync with the marketing-site state machine.
 */

const CONSENT_STORAGE_KEY = 'cookie_consent_v1';
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

interface StoredConsent {
  choice: 'accepted' | 'rejected' | { necessary: true; statistics: boolean };
  ts: number;
}

/**
 * True iff the user has a fresh consent decision that permits statistics
 * cookies (full "accepted" or granular `statistics: true`). Expired or absent
 * decisions, explicit rejections, and unreadable storage all return false.
 */
export function hasStatisticsConsent(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > CONSENT_TTL_MS) {
      return false;
    }
    if (parsed.choice === 'accepted') return true;
    if (parsed.choice === 'rejected') return false;
    return parsed.choice?.statistics === true;
  } catch {
    return false;
  }
}

/**
 * Run `callback` once, the first time statistics consent is granted after the
 * page has loaded (the marketing banner / in-app auto-grant dispatch the
 * `cookieConsent:accepted` CustomEvent). No-op on the server. Returns a cleanup
 * function that removes the listener.
 *
 * If consent is already present, the caller should act immediately — this only
 * covers the late opt-in case.
 */
export function onConsentAccepted(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback();
  window.addEventListener('cookieConsent:accepted', handler, { once: true });
  return () => window.removeEventListener('cookieConsent:accepted', handler);
}
